// Shared logic for the producer's saved crew ("My Team"). Both the
// single add/edit dialog and the bulk paste-grid build on these pure
// helpers so the two flows can't drift in how they parse gear, validate
// rows, or shape the Supabase payload. Kept free of React so it's unit
// testable in isolation.

import { resolveCrewRole, type Json } from "@produce/shared";

export const GEAR_OPTIONS = ["Film camera", "Photo camera", "Audio", "Computer", "Other"] as const;

export interface GearItem {
  type: string;
  detail: string;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  role: string | null;
  gear: GearItem[];
  created_at: string;
  updated_at: string;
}

export interface FormState {
  name: string;
  email: string;
  phone: string;
  role: string;
  gear: GearItem[];
}

export const emptyForm: FormState = { name: "", email: "", phone: "", role: "", gear: [] };

/** Normalize stored gear (legacy string[] or object[]) into GearItem[]. */
export const normalizeGear = (
  gear: Array<string | { type?: string; detail?: string }>,
): GearItem[] =>
  gear.map((g) =>
    typeof g === "string"
      ? { type: g, detail: "" }
      : { type: g.type || "", detail: g.detail || "" },
  );

/** Display label for a gear pill: "Type: detail" when a detail exists. */
export const formatGearBadge = (g: GearItem): string =>
  g.detail ? `${g.type}: ${g.detail}` : g.type;

/**
 * Parse a pasted gear cell into GearItem[]. The bulk grid lists the
 * actual kit by name, comma-separated — e.g. "RED, FX6, MacBook" →
 * [{RED}, {FX6}, {MacBook}]. No category is required; each entry is
 * stored as its own pill (detail stays empty).
 */
export const parseGearCell = (cell: string): GearItem[] => {
  if (!cell || !cell.trim()) return [];
  return cell
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((type) => ({ type, detail: "" }));
};

/** Collapse internal whitespace; returns "" for empty input. */
export const normalizePhone = (phone: string): string => phone.trim().replace(/\s+/g, " ");

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isValidEmail = (email: string): boolean => EMAIL_RE.test(email.trim());

/** Canonical email comparison key (trim + lowercase). */
export const emailKey = (email: string): string => email.trim().toLowerCase();

/** Canonical name comparison key (trim, collapse spaces, lowercase). */
export const nameKey = (name: string): string => name.trim().replace(/\s+/g, " ").toLowerCase();

/** Why a candidate can't be added: an existing member already matches. */
export type DuplicateReason = "email" | "name";

/**
 * Find whether a candidate (about to be added to My Team) already exists,
 * matching on email OR name. The DB only enforces email uniqueness, so
 * the name guard lives here. Email is checked first across the whole team
 * so an email clash is reported deterministically over a name clash.
 * Returns null when the candidate is safe to add.
 */
export const findTeamMemberDuplicate = (
  members: Array<Pick<TeamMember, "name" | "email">>,
  candidate: { name: string; email: string },
): DuplicateReason | null => {
  const email = emailKey(candidate.email);
  const name = nameKey(candidate.name);
  if (email && members.some((m) => emailKey(m.email) === email)) return "email";
  if (name && members.some((m) => nameKey(m.name) === name)) return "name";
  return null;
};

/**
 * Build the Supabase `team_members` insert/update payload from a form.
 * Trims, lowercases the email, nulls empties, and strips blank gear.
 */
export const buildTeamMemberPayload = (producerId: string, form: FormState) => {
  const gearJson = form.gear
    .filter((g) => g.type)
    .map((g) => ({ type: g.type, detail: g.detail })) as Json;
  return {
    producer_id: producerId,
    name: form.name.trim(),
    email: emailKey(form.email),
    phone: normalizePhone(form.phone) || null,
    role: form.role || null,
    gear: gearJson,
  };
};

// ---- Bulk paste-grid parsing & validation ----

export const BULK_COLUMNS = ["name", "email", "phone", "role", "gear"] as const;
export type BulkColumn = (typeof BULK_COLUMNS)[number];

/** One editable row in the bulk grid (raw strings as typed/pasted). */
export interface BulkRow {
  name: string;
  email: string;
  phone: string;
  role: string;
  gear: string;
}

export const emptyBulkRow = (): BulkRow => ({ name: "", email: "", phone: "", role: "", gear: "" });

/** True when every cell in the row is blank (ignoring whitespace). */
export const isBulkRowEmpty = (row: BulkRow): boolean =>
  !row.name.trim() &&
  !row.email.trim() &&
  !row.phone.trim() &&
  !row.role.trim() &&
  !row.gear.trim();

const HEADER_TOKENS = new Set([
  "name",
  "naam",
  "email",
  "e-mail",
  "mail",
  "phone",
  "telefoon",
  "tel",
  "role",
  "rol",
  "function",
  "functie",
  "gear",
  "equipment",
  "apparatuur",
]);

/**
 * True when a parsed row looks like a spreadsheet header (its first two
 * non-empty cells are known column labels) rather than crew data.
 */
const looksLikeHeader = (cells: string[]): boolean => {
  const filled = cells.map((c) => c.trim().toLowerCase()).filter(Boolean);
  if (filled.length === 0) return false;
  return filled.slice(0, 2).every((c) => HEADER_TOKENS.has(c));
};

/**
 * Parse pasted clipboard text (TSV from Excel/Sheets: rows by newline,
 * columns by tab) into BulkRows mapped onto BULK_COLUMNS. A leading
 * header row is detected and skipped. Fully-empty rows are dropped.
 */
export const parsePastedGrid = (clipboard: string): BulkRow[] => {
  const lines = clipboard.replace(/\r\n?/g, "\n").split("\n");
  const rows: BulkRow[] = [];
  let headerChecked = false;
  for (const line of lines) {
    const cells = line.split("\t");
    if (cells.every((c) => !c.trim())) continue;
    if (!headerChecked) {
      headerChecked = true;
      if (looksLikeHeader(cells)) continue;
    }
    const row = emptyBulkRow();
    BULK_COLUMNS.forEach((col, i) => {
      row[col] = (cells[i] ?? "").trim();
    });
    rows.push(row);
  }
  return rows;
};

const COLUMN_INDEX: Record<BulkColumn, number> = { name: 0, email: 1, phone: 2, role: 3, gear: 4 };

/** Parse clipboard text into a 2D grid of trimmed-by-line cells. */
const toGrid = (clipboard: string): string[][] => {
  const lines = clipboard.replace(/\r\n?/g, "\n").split("\n");
  // Drop a single trailing empty line (Excel appends one).
  if (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
  return lines.map((line) => line.split("\t"));
};

/**
 * Place pasted spreadsheet content into the grid starting at
 * (startRow, startCol), expanding rows as needed and filling columns
 * rightward from the focused column. When pasting a full block into the
 * top-left cell, a leading header row is detected and skipped. Returns a
 * new rows array (pure — does not mutate the input).
 */
export const pasteIntoGrid = (
  rows: BulkRow[],
  startRow: number,
  startCol: BulkColumn,
  clipboard: string,
): BulkRow[] => {
  let grid = toGrid(clipboard).filter((cells) => cells.some((c) => c.trim()));
  if (grid.length === 0) return rows;

  const startColIndex = COLUMN_INDEX[startCol];
  if (startRow === 0 && startColIndex === 0 && looksLikeHeader(grid[0])) {
    grid = grid.slice(1);
  }

  const next = rows.map((r) => ({ ...r }));
  grid.forEach((cells, r) => {
    const targetRow = startRow + r;
    while (next.length <= targetRow) next.push(emptyBulkRow());
    cells.forEach((value, c) => {
      const colIndex = startColIndex + c;
      const col = BULK_COLUMNS[colIndex];
      if (col) next[targetRow][col] = value.trim();
    });
  });
  return next;
};

export type RowSeverity = "valid" | "warning" | "error";
export type DuplicateResolution = "skip" | "update";

export interface ValidatedRow {
  index: number;
  raw: BulkRow;
  name: string;
  email: string;
  phone: string;
  /** Resolved (canonical-where-possible) role. */
  role: string;
  roleMatched: boolean;
  gear: GearItem[];
  severity: RowSeverity;
  errors: string[];
  warnings: string[];
  /** Set when this email already exists in the producer's team. */
  duplicateOf?: string;
}

export interface ValidateOptions {
  existingEmails: Set<string>;
}

/**
 * Validate bulk rows. Drops rows that are entirely empty. Per row:
 * required name + email, email format, duplicate within the batch,
 * duplicate against the existing team, role resolution and gear parsing.
 * Severity: error (blocks save), warning (savable but flagged), valid.
 */
export const validateRows = (rows: BulkRow[], options: ValidateOptions): ValidatedRow[] => {
  const seenInBatch = new Map<string, number>();
  const result: ValidatedRow[] = [];

  rows.forEach((raw, index) => {
    // Skip blank rows entirely.
    if (isBulkRowEmpty(raw)) return;

    const name = raw.name.trim();
    const email = raw.email.trim();
    const phone = normalizePhone(raw.phone);

    const errors: string[] = [];
    const warnings: string[] = [];

    if (!name) errors.push("Name is missing");
    if (!email) errors.push("Email is missing");
    else if (!isValidEmail(email)) errors.push("Invalid email address");

    const key = emailKey(email);
    if (email && isValidEmail(email)) {
      const firstSeen = seenInBatch.get(key);
      if (firstSeen !== undefined) {
        errors.push(`Duplicate of row ${firstSeen + 1}`);
      } else {
        seenInBatch.set(key, index);
      }
    }

    // Resolve the role onto a canonical value (so camera/photo functions
    // stay CAM ID-eligible) but do NOT warn on a miss: plenty of valid
    // roles (editor, producer, audio, runner, …) simply aren't camera
    // roles, and warning on each would be noise. The role datalist nudges
    // producers toward canonical spellings instead.
    const resolved = resolveCrewRole(raw.role);

    const gear = parseGearCell(raw.gear);

    let duplicateOf: string | undefined;
    if (email && isValidEmail(email) && options.existingEmails.has(key)) {
      duplicateOf = key;
      warnings.push("Email already in your team");
    }

    const severity: RowSeverity = errors.length ? "error" : warnings.length ? "warning" : "valid";

    result.push({
      index,
      raw,
      name,
      email: key,
      phone,
      role: resolved.role,
      roleMatched: resolved.matched,
      gear,
      severity,
      errors,
      warnings,
      duplicateOf,
    });
  });

  return result;
};

/** A validated row turned into a FormState (for payload building). */
export const validatedRowToForm = (row: ValidatedRow): FormState => ({
  name: row.name,
  email: row.email,
  phone: row.phone,
  role: row.role,
  gear: row.gear,
});

/** CSV template (header only) for the "Download template" button. */
export const BULK_CSV_TEMPLATE = "Name,Email,Phone,Role,Gear\n";

// ---- Search / filter / sort (client-side; the team is small) ----

export type TeamSortKey = "name" | "role" | "recent";

export interface TeamFilter {
  search: string;
  /** Selected role values; empty = all roles. */
  roles: string[];
  /** Selected gear types; empty = all gear. */
  gearTypes: string[];
  sort: TeamSortKey;
}

export const defaultTeamFilter: TeamFilter = { search: "", roles: [], gearTypes: [], sort: "name" };

/**
 * The distinct gear types actually present across the team, sorted.
 * Drives the gear filter so it reflects real data (categories AND the
 * free-form kit names entered via bulk add), not a fixed category list.
 */
export const distinctGearTypes = (members: TeamMember[]): string[] => {
  const set = new Set<string>();
  for (const m of members) {
    for (const g of m.gear) {
      if (g.type) set.add(g.type);
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b));
};

const matchesSearch = (m: TeamMember, q: string): boolean => {
  const haystack = [m.name, m.email, m.phone ?? "", m.role ?? "", ...m.gear.map((g) => g.type)]
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

/** Apply the search box, role/gear filters and sort to the member list. */
export const filterAndSortMembers = (members: TeamMember[], filter: TeamFilter): TeamMember[] => {
  const q = filter.search.trim().toLowerCase();
  const roleSet = new Set(filter.roles);
  const gearSet = new Set(filter.gearTypes);

  const filtered = members.filter((m) => {
    if (q && !matchesSearch(m, q)) return false;
    if (roleSet.size && !roleSet.has(m.role ?? "")) return false;
    if (gearSet.size && !m.gear.some((g) => gearSet.has(g.type))) return false;
    return true;
  });

  const sorted = [...filtered];
  if (filter.sort === "name") {
    sorted.sort((a, b) => a.name.localeCompare(b.name));
  } else if (filter.sort === "role") {
    sorted.sort(
      (a, b) => (a.role ?? "").localeCompare(b.role ?? "") || a.name.localeCompare(b.name),
    );
  } else if (filter.sort === "recent") {
    sorted.sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
  }
  return sorted;
};
